import { clamp, motion, AnimatePresence } from "framer-motion";
import { PropsWithChildren, useEffect, useRef, useState } from "react";

import {
  getCurrentSidebarWidth,
  useUpdateSeparatelyPersistedState,
} from "stores/UserPreferencesStore";
import { useEvent } from "client-util/useEvent";

import "./SidebarWrapper.css";
import { useInPuzzlePage, useParsedSlug } from "stores/NavStore";
import { usePuzzleData } from "stores/ScopeStateStore";
import { PuzzleType } from "sockapi/puzzle-data";

// TODO: onClick the resize to hide?

function SidebarWrapper({ children }: PropsWithChildren) {
  const { inPuzzle, inPiece } = useInPuzzlePage();
  const { puzName } = useParsedSlug();
  const { type } = usePuzzleData(puzName) ?? {};
  const showSidebar = type !== PuzzleType.STORY && (inPuzzle || inPiece);
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const sidebarWidth = useRef(getCurrentSidebarWidth());
  const updateSeparatelyPersistedState = useUpdateSeparatelyPersistedState();

  const onResizeStart = useEvent(() => {
    isResizing.current = true;
    if (sidebarRef.current) sidebarRef.current.style.transition = "none";
  });

  useEffect(() => {
    const onResize = (e: MouseEvent) => {
      if (isResizing.current && sidebarRef.current) {
        const newWidth = clamp(
          250,
          document.body.clientWidth - 250,
          e.clientX - sidebarRef.current.getBoundingClientRect().left
        );
        sidebarRef.current.style.width = `${newWidth}px`;
        sidebarWidth.current = newWidth;
        updateSeparatelyPersistedState({ sidebarWidth: newWidth });
      }
    };
    const onResizeEnd = () => {
      isResizing.current = false;
      if (sidebarRef.current) sidebarRef.current.style.transition = "";
    };

    window.addEventListener("pointermove", onResize);
    window.addEventListener("pointerup", onResizeEnd);
    return () => {
      window.removeEventListener("pointermove", onResize);
      window.removeEventListener("pointerup", onResizeEnd);
    };
  }, [updateSeparatelyPersistedState]);

  // This used to animate the width of the sidebar. Unfortunately, doing so
  // forces the page to relayout, meaning you can see images getting bigger and
  // text reflowing in the fraction of a second while the puzzle pane fades
  // out. Instead, we use transform and AnimatePresence to ensure the sidebar
  // is always either at its intended width or nonexistent.
  return (
    <AnimatePresence>
      {!showSidebar ? null : (
        <>
          <div className="sidebar-wrapper-placeholder" />
          <motion.div
            className="sidebar-wrapper-wrapper"
            key="sidebar-wrapper-wrapper"
            initial={{ transform: "translateX(-100%)" }}
            animate={{
              transform: isOpen
                ? "translateX(0)"
                : // should match width in css
                  "translateX(calc(-100% + 1em))",
            }}
            exit={{ transform: "translateX(-100%)" }}
            transition={{ duration: 0.15 }}
          >
            <div
              className="sidebar-wrapper"
              style={{
                width: `${sidebarWidth.current}px`,
              }}
              ref={sidebarRef}
            >
              {children}
            </div>
            <div
              className="resize-handle"
              onPointerDown={(evt) => {
                evt.preventDefault();
                if (!isOpen) return;
                onResizeStart();
              }}
            >
              <div
                className="resize-handle-button"
                onClick={() => {
                  if (isResizing.current) return;
                  setIsOpen(!isOpen);
                }}
                onPointerDown={(evt) => {
                  evt.stopPropagation();
                }}
              >
                〉
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default SidebarWrapper;
